import React, { useEffect, useRef } from 'react';

/** A play triangle in a ring, for Rematch. */
const PlayIcon = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <circle cx="16" cy="16" r="13" fill="none" stroke="currentColor" strokeWidth="2.4" />
    <path d="M13 10.5v11l9-5.5Z" fill="currentColor" />
  </svg>
);

/** A house, for Main Menu. */
const HomeIcon = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <path d="M4 15 16 5l12 10h-3.5v12h-6v-8h-5v8h-6V15Z" fill="currentColor" />
  </svg>
);

/**
 * EndScreen — the result of a match, over the victory scene.
 *
 * Laid out as the user's victory mock-up: a framed title plate in the
 * winner's colour with the scoreline under it, the champion on the roof in
 * the middle (drawn by the canvas — this layer leaves that space clear), and a
 * console at the bottom with the big Rematch button and Main Menu. Upright on
 * a phone the console takes the band the thumb controls had; everywhere else
 * it sits over the bottom of the arena.
 *
 * Everything it shows comes from endSummary in App.js, computed once as the
 * match ends, so the R key, the button and the words on it always agree about
 * what a rematch will do (carry the series on, put the same stake up again,
 * or a free rematch when the wallet no longer covers it).
 *
 * @param {object} props
 * @param {object} props.summary From endSummary.
 * @param {boolean} props.touch Primary pointer is a thumb: no key hints.
 * @param {boolean} props.band Upright touchscreen: the console fills the band.
 * @param {Function} props.onRematch
 * @param {Function} props.onMenu
 * @param {Function} props.onConsoleTop Reports how far down the page the
 *   console starts, in CSS px (Infinity when it is not over the arena), so the
 *   victory camera can keep the champion above it.
 */
const EndScreen = ({
  summary, touch, band, onRematch, onMenu, onConsoleTop,
}) => {
  const consoleRef = useRef(null);

  // The console's top edge, for the victory camera. Measured rather than
  // assumed: its height follows the type size and the stake line.
  useEffect(() => {
    const el = consoleRef.current;
    if (!el || !onConsoleTop) return undefined;
    const report = () => onConsoleTop(band ? Infinity : el.getBoundingClientRect().top);
    report();
    const watch = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(report) : null;
    if (watch) watch.observe(el);
    window.addEventListener('resize', report);
    return () => {
      if (watch) watch.disconnect();
      window.removeEventListener('resize', report);
      onConsoleTop(Infinity);
    };
  }, [band, onConsoleTop]);

  // Blur on click, as the other in-game buttons do: a focused button would
  // take the next keypress instead of the game.
  const tap = (fn) => (e) => { e.currentTarget.blur(); fn(); };

  const {
    disconnected, headline, team, score, detail, payout,
    rematchLabel, rematchNote, waitingForHost,
  } = summary;

  return (
    <div
      className={`end-screen${band ? ' band' : ''}`}
      style={{ '--team': team }}
      role="dialog"
      aria-label={headline}
    >
      <div className="end-title">
        <h2 className="end-headline">{headline}</h2>
        {score && (
          <div className="end-score">
            <span className="end-score-name green">{score.leftName}</span>
            <b className="green">{score.left}</b>
            <i aria-hidden="true" />
            <b className="red">{score.right}</b>
            <span className="end-score-name red">{score.rightName}</span>
          </div>
        )}
        {detail && <p className="end-detail">{detail}</p>}
        {payout && (
          <p className={`end-payout ${payout.won ? 'won' : 'lost'}`}>{payout.text}</p>
        )}
      </div>

      <div className="end-console" ref={consoleRef}>
        {!disconnected && (waitingForHost ? (
          <p className="end-waiting">Waiting for the host to rematch…</p>
        ) : (
          <button type="button" className="end-rematch" onClick={tap(onRematch)}>
            <span className="end-chev" aria-hidden="true">«</span>
            <PlayIcon />
            <span>{rematchLabel}</span>
            {!touch && <kbd>R</kbd>}
            <span className="end-chev" aria-hidden="true">»</span>
          </button>
        ))}
        {rematchNote && !disconnected && !waitingForHost && (
          <p className="end-note">{rematchNote}</p>
        )}
        <button type="button" className="end-menu" onClick={tap(onMenu)}>
          <HomeIcon />
          <span>Main menu</span>
          {!touch && <kbd>M</kbd>}
        </button>
      </div>
    </div>
  );
};

export default EndScreen;
